/**
 * One-line digest of the watchlist for the mobile Brief's "Active List" card (issue #314).
 *
 * A smart list's card carries that list's AI-written criteria text; the watchlist has no
 * criteria, so its card used to carry one fixed sentence that never changed and read as an
 * empty card. This derives the subtitle from the loaded rows instead.
 *
 * The accessors are injected rather than imported so the digest is built on the SAME
 * definitions the cards below it use — "today's move" is whatever the Your movers card
 * ranks on, and "reports this week" is whatever the Your upcoming earnings card lists —
 * so the card can never disagree with the feed it summarises. `rankByMove` / `topMovers`
 * live here for the same reason: the Your movers card and this digest call the same two
 * functions, so a ticker cannot lead one and be absent from the other.
 */

export const EARNINGS_HORIZON_DAYS = 7;

/** Movers named inside the digest line — fewer than the movers cards show, to stay one card. */
export const DIGEST_MOVERS_PER_SIDE = 2;

/** Reporters named before the line falls back to "and N others" (issue #316). */
export const DIGEST_REPORTERS_NAMED = 3;

/** Shared with the Your upcoming earnings card: a null/NaN or past date never qualifies. */
export const reportsWithinHorizon = (days, horizon = EARNINGS_HORIZON_DAYS) =>
  typeof days === 'number' && !Number.isNaN(days) && days >= 0 && days <= horizon;

/** UTC date of a row's quote, from `regularMarketTime` (a UNIX epoch in SECONDS); null without one. */
export const quoteSessionOf = (company) => {
  const t = company?.regularMarketTime;
  if (typeof t !== 'number' || !Number.isFinite(t) || t <= 0) return null;
  return new Date(t * 1000).toISOString().slice(0, 10);
};

export const newestSession = (companies) =>
  (Array.isArray(companies) ? companies : []).reduce((newest, company) => {
    const session = quoteSessionOf(company);
    return session && (!newest || session > newest) ? session : newest;
  }, null);

/**
 * Wrap a change accessor so only quotes from the NEWEST session among the rows count as
 * "today's move". `price_update_cron` does not reach every watchlist ticker every session
 * (54 of 188 still carried Friday's quote at Monday's close, issue #320), and a row's
 * `regular_market_change_percent` is that stale session's move — SMR led the decliners at
 * −15.67% three days after the fact. A row with no quote timestamp is excluded too; if NO row
 * carries one the accessor is returned ungated rather than blanking every card.
 */
export const todaysMoveOnly = (companies, changePercentOf) => {
  const newest = newestSession(companies);
  if (!newest) return changePercentOf;
  return (company) => (quoteSessionOf(company) === newest ? changePercentOf(company) : null);
};

/**
 * Rows with a live move, best first. A row whose accessor yields null (no quote) drops out
 * rather than entering the ranking on a wrong number.
 * @returns {Array<{company:object, change:number}>}
 */
export const rankByMove = (companies, changePercentOf) =>
  (Array.isArray(companies) ? companies : [])
    .map((company) => ({ company, change: changePercentOf(company) }))
    .filter(({ change }) => typeof change === 'number' && !Number.isNaN(change))
    .sort((a, b) => b.change - a.change);

/** Top `perSide` risers and fallers out of a `rankByMove` result; the worst decliner leads. */
export const topMovers = (ranked, perSide) => ({
  gainers: ranked.filter(({ change }) => change > 0).slice(0, perSide),
  // Reverse the tail rather than re-sorting, so the worst decliner leads.
  decliners: ranked.filter(({ change }) => change < 0).slice(-perSide).reverse(),
});

const finiteOr = (value, fallback) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/**
 * @returns {{count:number, up:number, down:number, reporting:object[],
 *            gainers:Array, decliners:Array} | null}
 *   null while nothing has loaded, so the caller can keep its fallback text.
 *   A row with no live quote counts toward `count` but toward neither `up` nor `down`;
 *   `null > 0` and `null < 0` are both false, and a flat 0 is neither up nor down either.
 *   `reporting` is every row reporting within the horizon, largest market cap first;
 *   a row with no cap is kept, after the ones that have one.
 */
export const buildWatchlistDigest = (
  companies,
  { changePercentOf, daysUntilEarningsOf, marketCapOf = () => null }
) => {
  if (!Array.isArray(companies) || !companies.length) return null;
  const ranked = rankByMove(companies, changePercentOf);
  const up = ranked.filter(({ change }) => change > 0).length;
  const down = ranked.filter(({ change }) => change < 0).length;
  const reporting = companies
    .filter((c) => reportsWithinHorizon(daysUntilEarningsOf(c)))
    .sort((a, b) => finiteOr(marketCapOf(b), -1) - finiteOr(marketCapOf(a), -1));
  return { count: companies.length, up, down, reporting, ...topMovers(ranked, DIGEST_MOVERS_PER_SIDE) };
};

/**
 * "Salesforce, Inc." → "Salesforce": the name people use, and no comma to collide with the
 * list separator. Only a TRAILING suffix is dropped, so "Corporate Office Properties" survives.
 */
const CORPORATE_SUFFIX = /[\s,]+(?:inc|inc\.|incorporated|corp|corp\.|corporation|co|co\.|company|ltd|ltd\.|limited|plc|p\.l\.c\.|n\.v\.|s\.a\.|ag|se|holdings|group)\.?$/i;
export const shortCompanyName = (company) => {
  const raw = typeof company?.name === 'string' ? company.name.trim() : '';
  const short = raw.replace(CORPORATE_SUFFIX, '').replace(/,$/, '').trim();
  return short || company?.tickerSymbol || '';
};

const reportersText = (reporting) => {
  if (!reporting.length) return 'none reporting this week';
  const named = reporting.slice(0, DIGEST_REPORTERS_NAMED).map((c) => `**${shortCompanyName(c)}**`);
  const others = reporting.length - named.length;
  const tail = others > 0 ? ` and ${others} other${others === 1 ? '' : 's'}` : '';
  return `Reporting this week: ${named.join(', ')}${tail}`;
};

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const moversText = (entries, arrow, formatPercent) =>
  entries.length
    ? `${arrow} ${entries.map(({ company, change }) => `**${company.tickerSymbol}** ${formatPercent(change)}`).join(', ')}`
    : null;

/**
 * @param formatPercent  the app's own percent formatter (signed), injected so the card's
 *                       numbers are spelled exactly like the movers cards' `meta`.
 */
export const formatWatchlistDigest = (
  { count, up, down, reporting = [], gainers = [], decliners = [] },
  formatPercent = (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}%`
) => [
  plural(count, 'ticker'),
  `${up} up / ${down} down today`,
  reportersText(reporting),
  moversText(gainers, '▲', formatPercent),
  moversText(decliners, '▼', formatPercent),
].filter(Boolean).join(' · ');
